import { useState, useCallback, useEffect } from 'react';
import * as favoritesService from '../services/favoritesService';
import {
  FavoritesData,
  FavoriteVessel,
  FavoriteLocation,
} from '../services/favoritesService';

/**
 * React hook for managing favorites
 * Provides state and operations for vessel/location favorites
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoritesData>({
    vessels: [],
    locations: [],
  });
  const [loading, setLoading] = useState(true);

  // Load favorites on mount
  useEffect(() => {
    const loaded = favoritesService.getFavorites();
    setFavorites(loaded);
    setLoading(false);
  }, []);

  // Refresh favorites from localStorage
  const refresh = useCallback(() => {
    const loaded = favoritesService.getFavorites();
    setFavorites(loaded);
  }, []);

  // Add vessel to favorites
  const addVessel = useCallback((vessel: FavoriteVessel) => {
    favoritesService.addVessel(vessel);
    refresh();
  }, [refresh]);

  // Add location to favorites
  const addLocation = useCallback((location: FavoriteLocation) => {
    favoritesService.addLocation(location);
    refresh();
  }, [refresh]);

  // Remove vessel from favorites
  const removeVessel = useCallback((registreringsmerke: string) => {
    favoritesService.removeVessel(registreringsmerke);
    refresh();
  }, [refresh]);

  // Remove location from favorites
  const removeLocation = useCallback((locationId: string) => {
    favoritesService.removeLocation(locationId);
    refresh();
  }, [refresh]);

  // Toggle vessel favorite status
  const toggleVessel = useCallback((vessel: FavoriteVessel): boolean => {
    const result = favoritesService.toggleVessel(vessel);
    refresh();
    return result;
  }, [refresh]);

  // Toggle location favorite status
  const toggleLocation = useCallback((location: FavoriteLocation): boolean => {
    const result = favoritesService.toggleLocation(location);
    refresh();
    return result;
  }, [refresh]);

  // Check if vessel is favorited
  const isVesselFavorited = useCallback((registreringsmerke: string): boolean => {
    return favoritesService.isVesselFavorited(registreringsmerke);
  }, []);

  // Check if location is favorited
  const isLocationFavorited = useCallback((locationId: string): boolean => {
    return favoritesService.isLocationFavorited(locationId);
  }, []);

  return {
    favorites,
    loading,
    addVessel,
    addLocation,
    removeVessel,
    removeLocation,
    toggleVessel,
    toggleLocation,
    isVesselFavorited,
    isLocationFavorited,
    refresh,
  };
}
